import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ShieldCheck, Star, ChevronDown } from "lucide-react";
import { CTAButton } from "@/components/shared/CTAButton";
import { HeroVideo } from "@/components/home/HeroVideo";
import { COMPANY } from "@/data/site";

const lines = [
  { text: "India's ", accent: "No.1" },
  { text: "Packers & Movers", accent: "" },
  { text: "Logistics Company", accent: "" },
];

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (d) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: d, ease: [0.22, 1, 0.36, 1] } }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const sceneY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <section
      ref={ref}
      className="relative w-full min-h-[640px] h-[90vh] max-h-[900px] overflow-hidden flex items-center"
      data-testid="hero"
    >
      {/* Cinematic cargo-ship video (Sora 2), falls back to real-photo slideshow */}
      <motion.div style={{ y: sceneY }} className="absolute inset-0 will-change-transform">
        <HeroVideo />
      </motion.div>

      {/* Brand overlays for text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-brand-deep/95 via-brand-deep/45 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-deep/50 via-transparent to-transparent" />
      <div className="grain absolute inset-0 opacity-[0.05]" />

      {/* Content */}
      <motion.div style={{ y: contentY }} className="container-x relative w-full">
        <div className="max-w-3xl">
          <motion.span
            initial="hidden" animate="show" custom={0} variants={fadeUp}
            className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-white mb-7"
          >
            <ShieldCheck className="h-4 w-4 text-brand-sky" /> Transport &amp; Relocation Solutions
          </motion.span>

          <h1 className="font-heading font-extrabold tracking-tighter text-white text-4xl sm:text-5xl md:text-6xl leading-[0.98]">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial="hidden"
                  animate="show"
                  custom={i}
                  variants={lineVariants}
                >
                  {line.text}
                  {line.accent && <span className="text-brand-sky">{line.accent}</span>}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial="hidden" animate="show" custom={0.7} variants={fadeUp}
            className="mt-5 font-heading font-semibold text-2xl sm:text-3xl text-brand-sky tracking-tight"
          >
            Where Every Mile Earns Your Trust.
          </motion.p>

          <motion.p
            initial="hidden" animate="show" custom={0.8} variants={fadeUp}
            className="mt-4 max-w-xl text-base sm:text-lg text-white/85 leading-relaxed"
          >
            For over 20 years, Agarwal Secure Packers &amp; Movers has helped families and businesses begin new chapters with safe, reliable, and stress-free relocations.
          </motion.p>

          <motion.div
            initial="hidden" animate="show" custom={0.9} variants={fadeUp}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <CTAButton to="/#quote" size="lg" variant="white" data-testid="hero-get-quote">Get Quote</CTAButton>
            <CTAButton
              to="/track"
              size="lg"
              arrow={false}
              variant="outlineLight"
              data-testid="hero-track"
            >
              Track Shipment
            </CTAButton>
            <a
              href={`tel:${COMPANY.mobiles[0]}`}
              className="group inline-flex items-center gap-2 text-white font-semibold hover:text-brand-sky transition-colors"
              data-testid="hero-call"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/30 group-hover:bg-white group-hover:text-brand transition-colors">
                <Phone className="h-4 w-4" />
              </span>
              Call Now
            </a>
          </motion.div>

          <motion.div
            initial="hidden" animate="show" custom={1.05} variants={fadeUp}
            className="mt-10 flex items-center gap-4"
          >
            <div className="flex -space-x-3">
              {["V", "A", "M", "S"].map((c) => (
                <span key={c} className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-brand text-sm font-bold ring-2 ring-brand">{c}</span>
              ))}
            </div>
            <div>
              <div className="flex items-center gap-1 text-brand-sky">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              </div>
              <p className="text-sm text-white/80">Rated by 200,000+ happy clients</p>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70"
      >
        <ChevronDown className="h-6 w-6 animate-bounce" />
      </motion.div>
    </section>
  );
};
